import { useEffect, useId, useState, type MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import { useHoursReport } from "@/hooks/useHoursReport";
import "./Hours.css";

/**
 * Approved hours, per person, over a window the farmer chooses.
 *
 * <p>
 * WHAT WAS MISSING. Approving a day was the end of the road for it. The review queue
 * filters `status = 'submitted'`, so the moment a row was decided it left every
 * owner-facing screen and did not come back, and the only place an approved figure
 * resurfaced was the roster's `hours_this_period` — a single cell that every
 * "Add approved hours to this period" press overwrites wholesale. Running one pay period
 * destroyed the figure for the one before it. There was no screen in the product that
 * answered "how many hours did I approve for this person".
 * </p>
 *
 * <p>
 * IT READS `time_entry`, WHICH KEEPS EVERY ROW — never `hours_this_period`, which keeps
 * one. That is the whole reason this is a report and not a column on the roster.
 * </p>
 *
 * <p>
 * THREE THINGS IT DELIBERATELY DOES NOT SAY, and the disclosure at the foot says them
 * out loud rather than leaving a reader to assume:
 * </p>
 * <ul>
 *   <li><b>Nothing about overtime.</b> A repo-wide grep for overtime / FLSA / 29 CFR
 *       returns zero hits: this product has taken no position on agricultural overtime,
 *       in sharp contrast to how carefully it sources the H-2A FICA exemption. So no
 *       figure here is labelled, split or coloured as overtime, and none may be until
 *       that determination is made.</li>
 *   <li><b>It is not a payroll ledger.</b> No pay_run table exists. This is what is in
 *       time_entry for the window, not what was filed.</li>
 *   <li><b>It does not know who still works here.</b> There is no active/terminated
 *       column on `employee`, so a window somebody worked no approved days in looks
 *       identical to one after they left. The report says what was approved; it does not
 *       claim to say who is employed.</li>
 * </ul>
 *
 * <p>
 * THE WINDOW ON SCREEN IS THE SERVER'S, NOT THIS COMPONENT'S. The first load asks for no
 * range at all and the pickers are seeded from what comes back, so the dates above the
 * figures are always the dates the figures were totalled over. A client that defaulted
 * its own fortnight would eventually print correct numbers under the wrong period — and
 * that period is the one the worker signed against.
 * </p>
 */
interface HoursProps {
  onNavigate: (path: string) => void;
}

export function Hours({ onNavigate }: HoursProps) {
  const { report, status, error, load } = useHoursReport();

  /* Real <a>, same modified-click passthrough AppShell.tsx's own handleNavClick uses. */
  function handleBackClick(event: MouseEvent<HTMLAnchorElement>): void {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate("/reports");
  }

  const fromId = useId();
  const toId = useId();

  /**
   * The pickers are SEEDED from the server's answer, not initialised to a guess. Empty
   * until the first report lands — an input pre-filled with a fortnight this client
   * invented would disagree with the figures beside it for the whole first paint.
   */
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    // Only until the farmer edits them. Re-seeding after that would drag their dates back
    // to the server's every time a report resolved, which is every time they pressed Show.
    if (report === null || touched) return;
    setFrom(report.start);
    setTo(report.end);
  }, [report, touched]);

  /**
   * The export is its own request, fired on the click.
   *
   * NOT fetched alongside the report. The response carries the whole CSV as a string, and a
   * year of a real roster's time is megabytes — paying that on every date change, for a
   * button most visits never press, would make the table slower to serve a download nobody
   * asked for yet.
   */
  const [exportState, setExportState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number; name: string } | { kind: "error"; message: string }
  >({ kind: "idle" });

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function downloadExport(): Promise<void> {
    if (rangeIsBackwards || from === "" || to === "") return;
    setExportState({ kind: "busy" });
    try {
      const file = await demoApi.exportTimesheets({ from, to });

      // The bytes are the server writer's output verbatim — this adds a blob and a filename
      // and nothing else, exactly as PaidLeave's wage-detail download does. Re-serialising
      // from rows here would make the download a second implementation of the file format.
      const blob = new Blob([file.delimitedFile], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.delimitedFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setExportState({ kind: "done", rows: file.rowCount, name: file.delimitedFileName });
    } catch (err) {
      setExportState({
        kind: "error",
        message: problemMessage(err, "Could not build the timesheet file."),
      });
    }
  }

  const totalApproved = report?.lines.reduce((sum, line) => sum + line.approvedHours, 0) ?? 0;
  const peopleWithHours = report?.lines.filter((line) => line.approvedDays > 0).length ?? 0;

  return (
    <main className="hours">
      {/* ef-044-1 (D2): Reports retires its tab strip for a searchable picker (ADR 0009
          §6) — this screen's way back is a plain link rather than a tab. */}
      <a href="/reports" className="hours__back" onClick={handleBackClick}>
        ← All reports
      </a>
      <PageHeader
        title="Approved hours"
        subtitle={
          report === null
            ? "Hours you have approved, per person"
            : `${formatWindow(report.start, report.end)} · ${peopleWithHours} of ${report.lines.length} with approved hours`
        }
      />

      {/*
        data-state is the tester's ground truth for this screen (rules/ui-breadcrumbs §1):
        loading / error / empty / ready read off the DOM rather than inferred from whether
        a particular sentence happens to be rendered. "empty" here means the report
        resolved and the roster has nobody on it — NOT that nobody had approved hours,
        which is a populated report full of zeroes and a completely different answer.
      */}
      <section
        className="hours__panel"
        aria-labelledby="hours-heading"
        data-state={status === "ready" && (report?.lines.length ?? 0) === 0 ? "empty" : status}
      >
        {/* ef-044-1: "Hours by employee", not "Approved hours by employee" — the
            PageHeader h1 above already reads "Approved hours", and
            `reports-picker.spec.ts`'s own generic /approved hours/i heading locator
            (asserting a report opened from the picker, not this screen's specific
            wording) needs exactly one match on this page. Not locked by hours.spec.ts,
            whose own `open()` helper matches the h1 with an anchored `/^approved
            hours$/i`. */}
        <h2 id="hours-heading" className="hours__heading">
          Hours by employee
        </h2>

        <form
          className="hours__range"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            void load({ from, to });
          }}
        >
          <div className="hours__field">
            <label className="hours__label" htmlFor={fromId}>
              From
            </label>
            <input
              id={fromId}
              className="hours__input"
              type="date"
              value={from}
              onChange={(event) => {
                setTouched(true);
                setFrom(event.target.value);
              }}
            />
          </div>

          <div className="hours__field">
            <label className="hours__label" htmlFor={toId}>
              To
            </label>
            <input
              id={toId}
              className="hours__input"
              type="date"
              value={to}
              /* Reported against the field the complaint is about, so the message lands
                 under the control that fixes it — the same placement rule the manual-entry
                 route's validation follows. */
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => {
                setTouched(true);
                setTo(event.target.value);
              }}
            />
          </div>

          <button type="submit" className="btn btn--primary hours__show" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Show hours"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="hours__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        {status === "loading" && <StateMessage tone="loading" title="Loading approved hours…" />}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the hours report."
            /* The figures are cleared rather than left standing under the new dates. A
               stale table under a window it was not totalled over is the one outcome that
               reads as an answer to the question just asked. */
            detail={`${error ?? ""} No figures are shown — a farm where nobody worked and a report that failed to load look identical, and only one of them is a fact.`}
          />
        )}

        {status === "ready" && report !== null && report.lines.length === 0 && (
          <StateMessage
            tone="empty"
            title="Nobody on the roster yet."
            detail="Hours appear here once you have people on the payroll and have approved some of their time."
          />
        )}

        {status === "ready" && report !== null && report.lines.length > 0 && (
          <>
            {/* The WRAPPER scrolls, never the page — same device as H2a's table, and the
                reason .hours__panel carries min-width: 0. */}
            <div className="hours__table-wrap" tabIndex={0} role="group" aria-labelledby="hours-heading">
            <table className="hours__table ruled-rows">
              <caption className="hours__caption">
                Approved hours for {formatWindow(report.start, report.end)}
              </caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Employee</th>
                  {/* DAYS, and the figure behind it counts distinct dates rather than
                      rows — two punches on one date are one day. See HoursReportLine. */}
                  <th scope="col" className="hours__num">
                    Days
                  </th>
                  <th scope="col" className="hours__num">
                    Approved hours
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.lines.map((line) => (
                  <tr
                    key={line.employeeId}
                    /* Zero approved days is a MEASUREMENT, and the row is marked as such
                       so a tester can tell it apart from a row that failed to render.
                       See HoursReportLine. */
                    data-state={line.approvedDays === 0 ? "none-approved" : "approved"}
                  >
                    <td className="hours__number">{line.employeeNumber}</td>
                    <th scope="row" className="hours__name">
                      {line.employeeName}
                    </th>
                    <td className="hours__num">{line.approvedDays}</td>
                    <td className="hours__num hours__hours">{formatHours(line.approvedHours)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td />
                  <th scope="row">Total</th>
                  <td className="hours__num">
                    {report.lines.reduce((sum, line) => sum + line.approvedDays, 0)}
                  </td>
                  <td className="hours__num hours__hours">{formatHours(totalApproved)}</td>
                </tr>
              </tfoot>
            </table>
            </div>

            <p className="hours__disclosure">
              These are hours you <strong>approved</strong> — submitted time waiting on you,
              and time you rejected, are not counted. The figures come from the time records
              themselves, so an earlier period stays readable after a later one has been added
              to the roster. This is <strong>not</strong> a filed payroll record, and it says
              nothing about overtime: EasyFarm does not yet compute agricultural overtime, so
              no total here is split or adjusted for it. Somebody showing{" "}
              <strong>0.00</strong> had no approved hours in this window — which is not the
              same as having left.
            </p>
          </>
        )}

        {/*
          THE DOWNLOAD SITS OUTSIDE THE READY BRANCH ON PURPOSE. A farmer whose report failed
          to load can still take the file — the two are separate requests, and disabling the
          export because a table did not render would withhold the artifact over a display
          problem. It needs only the two dates, which are on screen either way.
        */}
        <div className="hours__export">
          <h3 className="hours__export-title">Timesheets for your bookkeeper</h3>
          {/*
            SAYS WHAT THE FILE CONTAINS, because it is NOT what the table above contains and
            two artifacts from one date picker that differ by design is a trap otherwise. The
            table totals approved time; the file carries submitted and rejected rows too, with
            a Status column, so a bookkeeper can see what is still open.
          */}
          <p className="hours__export-copy">
            A spreadsheet of every recorded day in this window — including time still waiting
            on you and time you rejected, each marked with its status. That is more than the
            table above, which counts approved hours only. It does <strong>not</strong> include
            where anyone clocked in.
          </p>
          <button
            type="button"
            /* GHOST, not primary: "Show hours" is the primary action on this screen and two
               filled buttons side by side make neither read as the main one. There is no
               btn--secondary in this design system — checked, rather than assumed, after
               writing one that would have rendered with no variant styling at all. */
            className="btn btn--ghost hours__export-btn"
            disabled={exportState.kind === "busy" || rangeIsBackwards || from === "" || to === ""}
            onClick={() => void downloadExport()}
          >
            {exportState.kind === "busy" ? "Building the file…" : "Download timesheets (CSV)"}
          </button>

          {exportState.kind === "done" && (
            <p className="hours__export-result" role="status">
              {/*
                THE COUNT IS THE POINT OF THIS LINE. A browser's only feedback is that a file
                appeared, so an empty export and a broken one are the same silence — and a
                bookkeeper opening a header-only file cannot tell "no time in this window"
                from "something went wrong". Zero is stated as an answer.
              */}
              {exportState.rows === 0
                ? `No recorded time in this window — ${exportState.name} has its column headings and nothing else.`
                : `${exportState.rows} ${exportState.rows === 1 ? "record" : "records"} saved as ${exportState.name}.`}
            </p>
          )}

          {exportState.kind === "error" && (
            <p className="hours__export-error" role="alert">
              {exportState.message} Nothing was downloaded.
            </p>
          )}
        </div>
      </section>
    </main>
  );
}

/**
 * Two decimal places, always.
 *
 * Not `formatMoney` — these are hours, and dressing them as dollars on a payroll screen is
 * exactly the confusion this page cannot afford. Not a bare `toFixed` at the call site
 * either: "7.5" and "7.50" in one column reads as two different kinds of figure.
 */
function formatHours(hours: number): string {
  return hours.toFixed(2);
}

/**
 * "Aug 1 – Aug 15, 2026" from the server's two ISO dates.
 *
 * Parsed as UTC noon rather than handed straight to `new Date(iso)`, which reads a bare
 * `YYYY-MM-DD` as midnight UTC and renders the PREVIOUS day for every reader west of
 * Greenwich — a report labelled with the wrong fortnight in the one timezone this product
 * is actually used in.
 */
function formatWindow(start: string, end: string): string {
  const asDate = (iso: string) => new Date(`${iso}T12:00:00Z`);
  const day = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const dayAndYear = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${day.format(asDate(start))} – ${dayAndYear.format(asDate(end))}`;
}
